import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CreditCard,
  AccountBalance,
  CheckCircle,
  Schedule,
  MoneyOff,
  AttachMoney,
  Speed,
  VerifiedUser,
} from '@mui/icons-material';

const PaymentTypeStep = ({ selectedType, onTypeSelect }) => {
  const paymentTypes = [
    {
      id: 'GATEWAY',
      title: 'Online Payment',
      subtitle: 'Pay with Credit/Debit Card via NGenius',
      icon: CreditCard,
      color: 'primary',
      features: [
        { icon: Speed, text: 'Instant wallet topup', positive: true },
        { icon: VerifiedUser, text: 'Automatic approval', positive: true },
        { icon: MoneyOff, text: 'No gateway fees', positive: true },
      ],
      recommended: false,
    },
    {
      id: 'OFFLINE',
      title: 'Bank Transfer / Cash Deposit',
      subtitle: 'Transfer directly to Thaklis bank account',
      icon: AccountBalance,
      color: 'success',
      features: [
        { icon: MoneyOff, text: 'No gateway fees', positive: true },
        { icon: CheckCircle, text: 'Secure & Direct', positive: true },
        { icon: Schedule, text: '1-2 business days processing', positive: false },
      ],
      recommended: true,
    },
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
        Choose Payment Method
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Select how you'd like to add money to your wallet
      </Typography>

      <Grid container spacing={3}>
        {paymentTypes.map((type) => {
          const Icon = type.icon;
          const isSelected = selectedType === type.id;

          return (
            <Grid item xs={12} md={6} key={type.id}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  border: 2,
                  borderColor: isSelected ? `${type.color}.main` : 'divider',
                  bgcolor: isSelected ? `${type.color}.50` : 'background.paper',
                  transition: 'all 0.3s',
                  position: 'relative',
                  '&:hover': {
                    borderColor: `${type.color}.main`,
                    boxShadow: 4,
                    transform: 'translateY(-4px)',
                  }
                }}
                onClick={() => onTypeSelect(type.id)}
              >
                {type.recommended && (
                  <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
                    <Chip
                      label="Recommended"
                      color={type.color}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                )}

                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        bgcolor: `${type.color}.main`,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2
                      }}
                    >
                      <Icon sx={{ fontSize: 32 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight={600}>
                        {type.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {type.subtitle}
                      </Typography>
                    </Box>
                  </Box>

                  <List dense sx={{ mt: 2 }}>
                    {type.features.map((feature, index) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <FeatureIcon
                              sx={{
                                fontSize: 20,
                                color: feature.positive ? 'success.main' : 'text.secondary'
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature.text}
                            primaryTypographyProps={{
                              variant: 'body2',
                              color: feature.positive ? 'text.primary' : 'text.secondary'
                            }}
                          />
                        </ListItem>
                      );
                    })}
                  </List>

                  {isSelected && (
                    <Box
                      sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        color: `${type.color}.main`
                      }}
                    >
                      <CheckCircle sx={{ mr: 1, fontSize: 20 }} />
                      <Typography variant="body2" fontWeight={600}>
                        Selected
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default PaymentTypeStep;
